import { useEffect, useState } from "react";
import { CreditCard, Smartphone, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function CheckoutDialog({ item, children }) {
  // item: { type: 'product'|'course', slug, title, price }
  const [open, setOpen] = useState(false);
  const [methods, setMethods] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", method: "mtn", reference: "" });

  useEffect(() => {
    if (open && !methods) {
      api.get("/payments/methods").then(({ data }) => setMethods(data)).catch(() => {});
    }
  }, [open, methods]);

  const payCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        item_type: item.type, slug: item.slug, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  const payManual = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/orders/manual", {
        item_type: item.type, slug: item.slug, ...form,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied");
  };

  const reset = (v) => {
    setOpen(v);
    if (!v) setTimeout(() => { setDone(false); setForm({ name: "", email: "", phone: "", method: "mtn", reference: "" }); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="bg-ink-paper border-gold-500/25 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white tracking-wide">
            Checkout — {item.title}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-sm">
            Choose Mobile Money (Cameroon) or international card to complete your purchase.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-8 text-center" data-testid="manual-order-success">
            <CheckCircle2 size={48} className="text-gold-400 mx-auto" />
            <p className="mt-6 font-serif text-2xl text-white">Order received</p>
            <p className="mt-3 text-sm text-white/65 leading-relaxed">
              Thank you. We'll confirm your payment within 24 hours and grant access or dispatch your item.
              A confirmation will be sent to your email.
            </p>
            <Button onClick={() => reset(false)} className="mt-8 bg-gold-500 text-ink hover:bg-gold-400 font-semibold">Done</Button>
          </div>
        ) : (
          <Tabs defaultValue="momo" className="mt-2">
            <TabsList className="bg-ink border border-gold-500/20 w-full grid grid-cols-2">
              <TabsTrigger value="momo" data-testid="tab-momo" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70 gap-2">
                <Smartphone size={15} /> Mobile Money
              </TabsTrigger>
              <TabsTrigger value="card" data-testid="tab-card" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70 gap-2">
                <CreditCard size={15} /> Card
              </TabsTrigger>
            </TabsList>

            {/* Mobile Money / Bank (Cameroon) */}
            <TabsContent value="momo" className="mt-5">
              <p className="text-xs text-white/60 leading-relaxed">
                {methods?.note || "Send payment, then submit your details below."}
              </p>
              <div className="mt-4 space-y-2">
                <p className="text-[10px] uppercase tracking-[0.2em] text-gold-500">Pay to · {methods?.account_name}</p>
                {methods?.methods?.map((m) => (
                  <div key={m.code} className="flex items-center justify-between border border-gold-500/20 bg-ink px-4 py-2.5">
                    <div>
                      <p className="text-sm text-white">{m.label}</p>
                      <p className="text-xs text-gold-300">{m.detail}</p>
                    </div>
                    {m.code !== "bank" && (
                      <button onClick={() => copy(m.detail)} className="text-white/50 hover:text-gold-400" data-testid={`copy-${m.code}`}>
                        <Copy size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <form onSubmit={payManual} className="mt-5 space-y-3" data-testid="manual-order-form">
                <div className="grid grid-cols-2 gap-3">
                  <Input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="momo-name" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40" />
                  <Input required placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="momo-phone" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40" />
                </div>
                <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="momo-email" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40" />
                <div className="grid grid-cols-2 gap-3">
                  <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="momo-method" className="bg-ink border border-gold-500/25 text-white/90 px-3 py-2 text-sm outline-none focus:border-gold-400">
                    <option value="mtn">MTN MoMo</option>
                    <option value="orange">Orange Money</option>
                    <option value="bank">Bank Transfer</option>
                  </select>
                  <Input required placeholder="Transaction ref" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="momo-reference" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40" />
                </div>
                <Button type="submit" disabled={busy} data-testid="momo-submit" className="w-full bg-gold-500 text-ink hover:bg-gold-400 font-semibold">
                  {busy ? "Submitting…" : `Submit Payment · $${item.price}`}
                </Button>
              </form>
            </TabsContent>

            {/* Card (Stripe) */}
            <TabsContent value="card" className="mt-5">
              <p className="text-sm text-white/65 leading-relaxed">
                Pay securely by international card via Stripe. You'll be redirected to a secure checkout page.
              </p>
              <div className="mt-4 flex items-center justify-between border border-gold-500/20 bg-ink px-4 py-3">
                <span className="text-sm text-white/70">Total</span>
                <span className="font-serif text-2xl text-gold-400">${item.price}</span>
              </div>
              <Button onClick={payCard} disabled={busy} data-testid="card-pay" className="mt-4 w-full bg-gold-500 text-ink hover:bg-gold-400 font-semibold">
                {busy ? "Redirecting…" : "Pay with Card"}
              </Button>
              <p className="mt-3 text-center text-[11px] text-white/40">Test card 4242 4242 4242 4242 · any future date · any CVC</p>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
